/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

// libraries
import React from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import {
    object, string, func, bool, number,
} from 'prop-types';
import { Fade } from '@material-ui/core';
import LazyLoad from 'react-lazyload';
import { clubComponentType } from '../common/types';
import { snipePath, isSnipeImageActive } from '../common/commonProductPageHelper';
import SnipeRender from '../common/SnipeRender';
import SnipeRenderQuery from '../common/SnipeRenderQuery';
import ClubPDPAltImageSlider from './ClubPDPAltImageSlider';
import ClubPDPDialog from './ClubPDPDialog';
import ProductImageMagnifierUI from '../ProductImageUI/ProductImageMagnifierUI';
import { emitCustomTrackingEvent } from '../../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        background: theme?.palette?.white,
        [theme?.breakpoints?.down('md')]: {
            flexDirection: 'column',
            justifyContent: 'flex-start',
        },
    },

    productImage: {
        width: '100%',
        height: 'auto',
        [theme?.breakpoints?.down('md')]: {
            height: 'auto',
        },
    },

    productImageWrapperClub: {
        display: 'inline-block',
        margin: '0',
        minWidth: '200px',
        maxWidth: '600px',
        height: '100%',
        position: 'relative',
        [theme?.breakpoints?.down('md')]: {
            marginLeft: '20px',
        },
        [theme?.breakpoints?.down('sm')]: {
            marginLeft: 0,
            maxWidth: '100%',
        },
    },
    imageContainer: {
        position: 'relative',
        cursor: 'pointer',
        marginBottom: '0.625rem',
    },
    snipe: {
        position: 'absolute',
        bottom: '4px',
        right: '0',
        left: '0',
        width: '100%',
    },

    clubLengthHeadline: {
        color: theme.palette.cms?.primary_font_color || '#ffffff',
        position: 'absolute',
        textAlign: 'center',
        backgroundColor: 'rgba(47,47,47, .9)',
        bottom: '5px',
        right: '0',
        left: '0',
        padding: '5px',
        '&:hover': {
            backgroundColor: theme.palette.cms?.primary || '#a1001a',
        },
    },
    link: {
        color: 'blue',
        textDecoration: 'underline',
        '&:hover': {
            cursor: 'pointer',
        },
    },
    productImageWrapper: {
        display: 'flex',
        maxWidth: 'none',
        '@media screen and (max-width: 1024px)': {
            display: 'inline-block',
        },
        '& $imageContainer': {
            order: '2',
        },
        '& $link': {
            display: 'none',
        },
        '& $clubLengthHeadline': {
            display: 'none',
        },
    },
}));

const ClubPDPImageDesktop = ({
    modalOpen,
    handleState,
    product,
    cdnLink,
    clubComponents,
    getSlides,
    selectedComponentMonth,
    navigate,
    renderZoom,
    shouldRenderSnipe,
    getClubLengthDescription,
    presentation_family,
    renderSnipe,
    heroImage,
    personalizationFlag,
    setShippingModalOpen,
    showSnipeImage,
    isClubPDPV2,
}) => {
    const dispatch = useDispatch();
    const trackProductImageClick = () => {
        dispatch(emitCustomTrackingEvent({
            eventType: 'custom',
            eventSubType: 'feature-tracking',
            action: 'Click',
            category: 'Product Image',
            label: 'Click on Image',
        }));
    };
    const classes = useStyles();
    return (
        <div className={classes.root}>
            <ClubPDPDialog
                modalOpen={modalOpen}
                handleState={handleState}
                cdnLink={cdnLink}
                clubComponents={clubComponents}
                getSlides={getSlides}
                selectedComponentMonth={selectedComponentMonth}
                navigate={navigate}
            />
            <div className={isClubPDPV2 ? `${classes.productImageWrapper} ${classes.productImageWrapperClub}` : classes.productImageWrapperClub}>
                <div
                    data-testid="hero-image-click"
                    onClick={() => { trackProductImageClick(); if (!isClubPDPV2) { handleState({ modalOpen: true, selectedComponentMonth: 0 }); } }}
                    onKeyDown={() => { }}
                    role="button"
                    tabIndex="0"
                    className={classes.imageContainer}
                >
                    {renderZoom()
                        ? (
                            <ProductImageMagnifierUI
                                imagePath={heroImage}
                                data={product}
                                classes={classes}
                                isRenderSnipe={renderSnipe && showSnipeImage}
                                personalizationFlag={personalizationFlag}
                            />
                        ) : (
                            <img src={heroImage} alt={product?.name} className={classes?.productImage} />
                        )}
                    {presentation_family === 'food' && showSnipeImage && renderSnipe && shouldRenderSnipe(product?.image?.snipe, personalizationFlag) && isSnipeImageActive(product?.image, 'pdp') /* data?.image && data?.image.snipe */ ? ( // if snipe data exsist display snipe img
                        <LazyLoad>
                            <Fade in timeout={700}>
                                <SnipeRender
                                    altText={product?.image?.altText}
                                    styleProp={classes.snipe}
                                    src={`${product?.image?.snipeImagePath}${product?.image?.snipe}_z.gif`}
                                    fallbackSrc={`${snipePath(product.image.path)}${product?.image?.snipe}_z.gif`}
                                />
                            </Fade>
                        </LazyLoad>
                    ) : null}
                    {showSnipeImage && <SnipeRenderQuery  partNumber={product?.partNumber}  productType="product" />}
                    {getClubLengthDescription() && (
                        <div className={classes.clubLengthHeadline}>
                            {getClubLengthDescription()}
                        </div>
                    )}
                </div>
                <ClubPDPAltImageSlider
                    itemsToShow={4}
                    itemsPerSlide={4}
                    data={getSlides(clubComponents, true)}
                    nextArrow="https://cdn1.harryanddavid.com/wcsstore/HarryAndDavid/images/navigator.svg"
                    prevArrow="https://cdn1.harryanddavid.com/wcsstore/HarryAndDavid/images/navigator.svg"
                    isClubPDPV2={isClubPDPV2}
                />
                <p className={classes.link} aria-hidden="true" onClick={() => setShippingModalOpen(true)}>Shipping and Processing Details</p>
            </div>
        </div>
    );
};
ClubPDPImageDesktop.defaultProps = {
    getSlides: () => { },
    modalOpen: false,
    handleState: () => { },
    product: {},
    cdnLink: '',
    selectedComponentMonth: 0,
    navigate: () => { },
    renderZoom: () => { },
    shouldRenderSnipe: () => { },
    getClubLengthDescription: () => { },
    presentation_family: 'food',
    renderSnipe: func,
    heroImage: '',
    personalizationFlag: false,
    setShippingModalOpen: () => {},
    showSnipeImage: false,
    isClubPDPV2: false,
};

ClubPDPImageDesktop.propTypes = {
    clubComponents: clubComponentType.isRequired,
    getSlides: func,
    modalOpen: bool,
    handleState: func,
    product: object,
    cdnLink: string,
    selectedComponentMonth: number,
    navigate: func,
    renderZoom: func,
    shouldRenderSnipe: func,
    getClubLengthDescription: func,
    presentation_family: string,
    renderSnipe: func,
    heroImage: string,
    personalizationFlag: bool,
    setShippingModalOpen: func,
    showSnipeImage: bool,
    isClubPDPV2: bool,
};

export default ClubPDPImageDesktop;
